import {
  DealerApiCommunications,
  redirectToLoginPage,
  setDealerTokenProvider
} from '../../webmodule-common/other/api/dealer-api-communications.js';
import { getCurrentUser, setAfterApplyUserEvent } from '../../webmodule-common/other/api/current-user.js';
import { registerComponents } from '../../webmodule-common/components/src/components/register-components.js';
import { registerComponentsLocal } from '../../webmodule-common/other/ui/templatecontrols/component-registry.js';
import { registerLangFile, tlang } from '../../webmodule-common/other/language/lang.js';
import { registerTechnicalDictionary } from '../../webmodule-common/other/language/technical-dictionary.js';

import { appConfig } from './app-config.js';
import { bindApplicationFeatures } from '../../webmodule-common/other/bind-application-features.js';
import {
  clearCurrentUserFromSession,
  saveCurrentUserIntoSession,
  verifySession
} from '../../webmodule-common/other/user-session-verifier.js';
import { dealerVerifyUser, performPageLoadLoginVerifications } from '../api/dealer-pageload-verifications.js';
import { DefaultLangBuilder } from '../../localization/default-lang-builder.js';
import { displayAllError } from '../../webmodule-common/other/ui/modal-errorhandler.js';
import { getApi, setApiFactory, setApiInjector } from '../api/api-injector.js';
import { interpolate } from '../../localization/global.js';
import { intializeLocalizationServices } from '../../localization/initialize-services.js';
import { LicenseServerAPI } from '../api/licenseserver-api.js';
import { runGlobalInit } from '../../webmodule-common/other/app-global-init.js';
import { ApiError, ProblemTypes } from '../../webmodule-common/interop/problem-details.js';
import { setCacheRegistry } from './cache-impl/cache-registry.js';
import { setErrorDialogEventHandler, showError } from '../../webmodule-common/other/ui/show-error.js';
import { setLocalizeInjector } from '../../webmodule-common/components/src/common/localize-injector.js';
import { setRoutes } from '../../webmodule-common/other/router.js';
import { showDevelopmentError } from '../../webmodule-common/other/development-error.js';
import { UserProfileCache } from './cache-impl/user-profile-cache.js';
import type { EventSnippet } from '../../webmodule-common/interop/types/misc.js';
import type { LanguageResourceData } from '../../localization/language-item-data.js';
import type { ValidationError } from '../../scriptsSupplier/api/validation-error.js';

let _appStartupEvents: Promise<void> | undefined;

export function getApplicationStartupEvents() {
  if (_appStartupEvents === undefined) {
    _appStartupEvents = applicationStartupEvents();
  }
  return _appStartupEvents;
}

globalThis.getApplicationStartupEvents = getApplicationStartupEvents;

async function applicationStartupEvents() {
  const log = (msg: string) => console.log('STARTUP: ' + msg);
  dealerBuildApplicationBindings();
  if (!(await dealerVerifyUser(log))) {
    window.location.reload();
  }
  await dealerPrepUserData(log);
}

async function dealerPrepUserData(_updateNote: (msg: string) => void) {
  try {
    //we want to enforce that the system the user connects to is fully up to date. this is a
    //simple call to the v6 systems to check their version, and will ensure the system resets info
    //as needed. This is also required for the quotes page, so we know if we are a multi supplier view or not
  } catch {
    console.log('could not load suppliers');
  }
}

async function connectUserAfterLogin() {
  const reset = async () => {
    clearCurrentUserFromSession();
    localStorage.removeItem('PAT-in-use');
    redirectToLoginPage();
  };
  const user = getCurrentUser();
  if (user !== null) {
    if (!(await verifySession())) {
      await reset();
      return;
    }

    //DO NOT MAKE ANY CALLS THAT NEED AUTHORIZATION BEFORE CONNECTING THE USER
    //Make any api calls or anything else here that are necessary to be used for the current user
    try {
      //this will update the user claims security before we rebind the router.

      saveCurrentUserIntoSession();
    } catch (e) {
      await showDevelopmentError(e as Error);
      await reset();
    }
  } else {
    clearCurrentUserFromSession();
    localStorage.removeItem('PAT-in-use');

    redirectToLoginPage();
    //we always want to login again
  }
}

export function rebindRouter() {
  const config = appConfig();
  setRoutes(config.routes);
  /*
                        addURLResolvers(
                            config.routes
                                .filter((x) => x.resolveUrl !== undefined)
                                .map((x) => {
                                    return x.resolveUrl as ResolveURLEntry;
                                })
                        );
                        */
}

function defaultLangGlossary() {
  const lang: LanguageResourceData = {
    name: 'glossary',
    language: 'en',
    items: [
      {
        id: 'glossary.user',

        segments: {
          __default__: 'User'
        }
      }
    ]
  };
  return lang;
}

function dealerBuildApplicationBindings() {
  setLocalizeInjector({
    interpolate: (id: string, data?: Record<string, any>) => {
      return interpolate(id, data);
    }
  });
  intializeLocalizationServices(
    [],
    [defaultLangGlossary()],
    [],
    () => DefaultLangBuilder.licenseServerDefault(),
    false
  );

  registerTechnicalDictionary(globalThis.dealerConfiguration.dictItems);
  registerLangFile(globalThis.dealerConfiguration.langFile);

  registerComponents();
  registerComponentsLocal();

  bindApplicationFeatures();

  setAfterApplyUserEvent(connectUserAfterLogin);

  setDealerTokenProvider(() => '');

  const errorDialogEventHandler = async (item: Error | ApiError, title: EventSnippet) => {
    try {
      await displayAllError(title, item);
    } catch (e) {
      console.log(e);
    }
  };
  setErrorDialogEventHandler(errorDialogEventHandler);

  setApiFactory({
    licenseServer: () => new LicenseServerAPI(getApi())
  });

  let _commSingleton: DealerApiCommunications | undefined;
  const apiInjecterEvent = () => {
    if (!_commSingleton)
      _commSingleton = new DealerApiCommunications(
        '',
        async (_response: ApiError | ValidationError[]) => {},
        () => {
          //Redirect to home page, next query will force a login to occur
          window.location.href = '/login';
        }
      );
    return _commSingleton;
  };
  //Dependency inject an api for the entire application
  setApiInjector(apiInjecterEvent);
  setCacheRegistry(() => {
    const api = getApi();
    return {
      userProfile: new UserProfileCache(api)
    };
  });

  window.addEventListener('unhandledrejection', function (event) {
    if (event.reason.message.includes('Vaadin')) {
      return;
    }
    console.error(event.reason.stack);
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    showError(
      new ApiError({
        type: ProblemTypes.InternalError,
        title: tlang`unhandled error`,
        status: 0,
        detail: event.reason.message,
        stackTrace: event.reason.stack
      }),
      () => tlang`Unhandled Error inside a promise occurred`
    );
  });

  runGlobalInit();
}

if (!performPageLoadLoginVerifications) console.log('missing page loader');
